import React, { FunctionComponent, memo, useEffect } from 'react';
import { RecoilRoot } from 'recoil';
import { BrowserRouter as Router } from 'react-router-dom';
import { PlumeUIConfig, PlumeUITheme } from 'plume-ui';

import AppContainer from './AppContainer';
import { initializePersistentState } from './persistent-state/persistent-state';
import PersistentStateRoot from './persistent-state/PersistentStateRoot';
import i18n from './utils/i18n';
import dayjs from 'dayjs';

declare global {
  interface Date {
    addHours: (hours: number) => Date;
    addDays: (days: number) => Date;
    isLeapYear: (year: number) => boolean;
    getDaysInMonth: () => number;
    addMonths: (months: number) => Date;
  }
}

const isLeapYear = (year: number) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getDaysInMonth = (year: number, month: number) => {
  return [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month];
};

Date.prototype.addHours = function (hours: number) {
  this.setTime(this.getTime() + hours * 60 * 60 * 1000);
  return this;
};

Date.prototype.addDays = function (days: number) {
  this.setTime(this.getTime() + days * 24 * 60 * 60 * 1000);
  return this;
};

Date.prototype.isLeapYear = function () {
  return isLeapYear(this.getFullYear());
};

Date.prototype.getDaysInMonth = function () {
  return getDaysInMonth(this.getFullYear(), this.getMonth());
};

Date.prototype.addMonths = function (months: number) {
  const date = this.getDate();
  this.setDate(1);
  this.setMonth(this.getMonth() + months);
  this.setDate(Math.min(date, this.getDaysInMonth()));
  return this;
};

const App: FunctionComponent = () => {
  useEffect(() => {
    i18n.on('languageChanged', (lang) => {
      dayjs.locale(lang);
    });

    return () => {
      i18n.off('languageChanged');
    };
  }, []);

  return (
    <PlumeUIConfig theme={PlumeUITheme.Dark}>
      <RecoilRoot
        initializeState={({ set }) => {
          initializePersistentState(set);
        }}
      >
        <PersistentStateRoot>
          <Router>
            <AppContainer />
          </Router>
        </PersistentStateRoot>
      </RecoilRoot>
    </PlumeUIConfig>
  );
};

export default memo(App);
